import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { BillingContractBillModel, ContractModel } from "src/api/contract/contract-types";
import { usePartnerNavigate } from "src/api/hooks/usePartnerNavigate";
import { BillItemByOrder, filterBillList, isBillPaySuccess } from "../../bill/contract-bill-types";

type Props = {
  contract?: ContractModel | null;
  billingContractBill?: BillingContractBillModel | null;
};
/**
 * 마이페이지 메인 > 신청/계약 내역 > 상세 화면 > 나의 납부 상태
 * (청구금액이 하나라도 유료 (0원 초과)일 경우만 노출)
 * (F/O 타입 이고, 계약체결 / 이용승인 / 이용중 / 이용완료 / 해지접수 / 해지완료 일 경우 노출)
 */
const MyPaymentStatus = ({ contract, billingContractBill }: Props) => {
  const navigate = usePartnerNavigate();
  const { t } = useTranslation("contract");
  // 모든 청구서의 청구금액이 0원인지 여부
  const isEveryBillAmountZero = useMemo(() => {
    return (billingContractBill?.billList || []).every((bill) => Number(bill?.originBill.totalAmount || 0) === 0);
  }, [billingContractBill]);

  // 회차별 청구 목록
  const billItemListByOrder: BillItemByOrder[] = useMemo(() => {
    // 전체 청구 목록
    const billList = filterBillList(billingContractBill?.billList);
    if (billList.length === 0) return [];

    // 회차별 청구 목록
    const _billItemListByOrder: BillItemByOrder[] = [];
    for (let i = 0; i < billList.length; i++) {
      const bill = billList[i];
      if (!_billItemListByOrder.find((_billItem) => _billItem.billOrder === bill.billOrder)) {
        const _billItemByOrder: BillItemByOrder = {
          billOrder: bill.billOrder!,
          bill: billList.find((_bill) => _bill.billOrder === bill.billOrder && !_bill.originBill.isOverdueBill)!, // 해당 회차의 원청구
          overdueBill: billList.find((_bill) => _bill.billOrder === bill.billOrder && _bill.originBill.isOverdueBill)!, // 해당 회차의 연체청구
        };
        _billItemListByOrder.push(_billItemByOrder);
      }
    }
    return _billItemListByOrder.sort((a, b) => b.billOrder - a.billOrder);
  }, [billingContractBill]);

  //
  // 나의 납부상태 미납 여부
  const isNonPayment: boolean = useMemo(() => {
    // 청구 내역이 없을때 납부
    if (billItemListByOrder.length === 0) return false;
    // 미납된 것들만 필터링
    const nonPayments = billItemListByOrder.filter((billItem) => {
      // 원청구는 납부

      if (isBillPaySuccess(billItem.bill?.billPayStatus)) {
        // 연체료 납부 여부
        if (billItem.bill.overduePayStatus === null) {
          return false;
        } else {
          return !isBillPaySuccess(billItem.bill.overduePayStatus);
        }

        // if (billItem?.overdueBill) {
        //   // 연체청구가 납부상태인지
        //   return !isBillPaySuccess(billItem.overdueBill?.billPayStatus);
        // } else {
        //   // 원청구 실시간 연체료 여부
        //   return Number(billItem.bill?.overdueAmount || 0) > 0;
        // }
      }
      // 원청구 미납
      return true;
    });
    return nonPayments.length > 0;
  }, [billItemListByOrder]);

  //

  const rentalTypeSchedule = useMemo(() => {
    if (contract && contract.scheduleList.length > 0) {
      const rentalSchedule = contract?.scheduleList.find((item) => item.supplyType === "RENTAL");
      if (rentalSchedule) {
        return rentalSchedule;
      }
    }
  }, [contract]);

  // RENTAL 스케줄 하위 청구서 중 신용카드 결제 건이 1건이라도 존재하는지
  const hasCreditcardBill = useMemo(() => {
    if (!billingContractBill?.billList || !rentalTypeSchedule?.scheduleId) return false;
    return billingContractBill.billList.some(
      (bill) =>
        bill.originBill?.pgcode === "creditcard" &&
        String(bill.originBill?.scheduleId) === String(rentalTypeSchedule.scheduleId),
    );
  }, [billingContractBill, rentalTypeSchedule]);

  if (!contract || !contract?.contractStep) return null;

  if (
    !isEveryBillAmountZero &&
    contract?.spaceProductType !== "TIME_COURT" &&
    ["CONTRACT_ACCEPT", "USE_APPROVAL", "USE_PROGRESS", "USE_COMPLETE", "TERMINATE_RECEIVED", "TERMINATE_COMPLETE"].includes(
      contract?.contractStep || "",
    )
  ) {
    return (
      <section className="base-section-layout top-divider">
        <div className="base-section-layout__container">
          <div className="base-section-layout__wrap mb30 pb0">
            {["CONTRACT_ACCEPT", "USE_APPROVAL", "USE_PROGRESS"].includes(contract?.contractStep || "") && billItemListByOrder.length === 0 ? (
              // 계약체결, 이용승인, 이용중 이고 청구목록이 없을 경우 (1회차가 도래하지 않아)
              <>
                <div className="base-section-layout__title-wrap">
                  <h2>{t("myPayment.title")}</h2>
                  <div className={`base-chip color-gray`}>
                    <span>{t("myPayment.statusPending")}</span>
                  </div>
                </div>
                <p className="pb10 font14 text-primary3 lh-20">
                  <Trans t={t} i18nKey="myPayment.checkBillingInfo" components={{ b: <b /> }} />
                </p>
              </>
            ) : (
              <>
                <div className="base-section-layout__title-wrap">
                  <h2>{t("myPayment.title")}</h2>
                  <div className={`base-chip ${isNonPayment ? "color-red" : "color-gray"}`}>
                    <span>{isNonPayment ? t("myPayment.statusUnpaid") : t("myPayment.statusPaid")}</span>
                  </div>
                </div>
                {isNonPayment ? (
                  <p className="pb10 font14 text-primary3 lh-20">
                    {t("myPayment.longOverdueNotice")}
                    <br /> <Trans t={t} i18nKey="myPayment.checkBillingInfo" components={{ b: <b /> }} />
                  </p>
                ) : (
                  <p className="pb10 font14 text-primary3 lh-20">
                    <Trans t={t} i18nKey="myPayment.checkBillingInfo" components={{ b: <b /> }} />
                  </p>
                )}
              </>
            )}

            <div className="pt20 d-flex flex-center">
              <button
                type="button"
                className="base-btn color-white size-contract "
                onClick={() => navigate(`/court/mypage/contracts/${contract?.contractId}/paymentSchedule`)}
              >
                {t("myPayment.paymentSchedule")}
              </button>
              <button
                type="button"
                className="base-btn color-white size-contract ml14"
                onClick={() => navigate(`/court/mypage/contracts/${contract?.contractId}/bills`)}
              >
                {t("myPayment.billingHistory")}
              </button>

              {/* RENTAL 스케줄 하위 청구서 중 신용카드 결제 건이 1건이라도 존재할 때 노출 */}
              {hasCreditcardBill && (
                <button
                  type="button"
                  className="base-btn color-white size-contract ml14"
                  onClick={() => navigate(`/court/mypage/contracts/${contract?.contractId}/payments`)}
                >
                  {t("myPayment.paymentHistory")}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }
  return null;
};

export default MyPaymentStatus;
